import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiUser, FiArrowRight, FiBookOpen, FiTrendingUp } = FiIcons;

function Blog() {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [articlesRef, articlesInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const featuredArticles = [
    {
      title: 'Understanding South Africa\'s New Employment Equity Regulations: What Employers Need to Know',
      excerpt: 'A comprehensive guide to the latest employment equity regulations and their impact on South African businesses. Learn about compliance requirements and best practices.',
      author: 'Advocate Nomsa Mthembu',
      date: 'December 15, 2024',
      readTime: '8 min read',
      category: 'Employment Law',
      image: 'https://media.phst.co.za/file/gnt-blog1.jpg'
    },
    {
      title: 'Corporate Governance Best Practices for SMEs in 2025',
      excerpt: 'Essential corporate governance principles that small and medium enterprises should implement to ensure sustainable growth and regulatory compliance.',
      author: 'Advocate Given Ndlovu',
      date: 'December 10, 2024',
      readTime: '6 min read',
      category: 'Corporate Law',
      image: 'https://media.phst.co.za/file/gnt-blog2.jpg'
    },
    {
      title: 'The Impact of POPIA on Employment Contracts: A Practical Guide',
      excerpt: 'How the Protection of Personal Information Act affects employment contracts and what employers need to do to ensure compliance.',
      author: 'Advocate Sipho Radebe',
      date: 'December 5, 2024',
      readTime: '10 min read',
      category: 'Employment Law',
      image: 'https://media.phst.co.za/file/gnt-blog3.jpg'
    }
  ];

  const recentArticles = [
    {
      title: 'Navigating Commercial Disputes: When to Consider Alternative Resolution',
      excerpt: 'Exploring the benefits of mediation and arbitration in commercial disputes.',
      author: 'Advocate Thibela Steenberg',
      date: 'November 28, 2024',
      readTime: '7 min read',
      category: 'Commercial Litigation'
    },
    {
      title: 'BBBEE Compliance: Building Sustainable Transformation Strategies',
      excerpt: 'A strategic approach to BBBEE compliance that goes beyond scoring points.',
      author: 'Advocate Sipho Radebe',
      date: 'November 20, 2024',
      readTime: '9 min read',
      category: 'Employment Law'
    },
    {
      title: 'International Trade Regulations: Key Updates for 2024',
      excerpt: 'Important changes in international trade law affecting South African businesses.',
      author: 'Advocate Priya Sharma',
      date: 'November 15, 2024',
      readTime: '5 min read',
      category: 'Regulatory Law'
    },
    {
      title: 'Contract Law Essentials: Protecting Your Business Interests',
      excerpt: 'Key elements every business contract should include for maximum protection.',
      author: 'Advocate Thibela Steenberg',
      date: 'November 8, 2024',
      readTime: '6 min read',
      category: 'Contract Law'
    }
  ];

  const categories = [
    'All Categories',
    'Corporate Law',
    'Employment Law',
    'Commercial Litigation',
    'Regulatory Law',
    'Contract Law'
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section ref={heroRef} className="relative bg-gradient-to-br from-navy-800 to-navy-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="font-opensans font-light text-4xl lg:text-6xl mb-6 text-white">
              Legal <span className="text-gold-600">Insights</span>
            </h1>
            <p className="font-opensans text-xl text-gray-200 max-w-3xl mx-auto">
              Stay informed with the latest legal developments, expert analysis, and practical guidance from our experienced attorneys. Knowledge is power in the legal world.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Articles Section */}
      <section ref={articlesRef} className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={articlesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-playfair font-bold text-4xl text-navy-800 mb-4">
              Featured Articles
            </h2>
            <p className="font-opensans text-xl text-gray-600 max-w-3xl mx-auto">
              Our latest insights on important legal developments affecting businesses and individuals in South Africa.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
            {featuredArticles.map((article, index) => (
              <motion.article
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={articlesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <div className="flex items-center space-x-4 mb-4">
                    <span className="bg-gold-600 text-white px-3 py-1 rounded-full text-xs font-opensans font-medium">
                      {article.category}
                    </span>
                    <span className="text-gray-500 text-sm font-opensans">
                      {article.readTime}
                    </span>
                  </div>
                  <h3 className="font-playfair font-bold text-xl text-navy-800 mb-3 line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="font-opensans text-gray-600 mb-4 line-clamp-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={FiUser} className="w-4 h-4 text-gray-400" />
                      <span className="font-opensans text-sm text-gray-600">
                        {article.author}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-500">
                      <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                      <span className="font-opensans text-sm">
                        {article.date}
                      </span>
                    </div>
                  </div>
                  <button className="mt-4 inline-flex items-center text-gold-600 font-opensans font-semibold hover:text-gold-700 transition-colors">
                    Read More
                    <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-2" />
                  </button>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Articles Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Articles List */}
            <div className="lg:w-2/3">
              <h2 className="font-playfair font-bold text-3xl text-navy-800 mb-8">
                Recent Articles
              </h2>
              <div className="space-y-8">
                {recentArticles.map((article, index) => (
                  <motion.article
                    key={index}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
                  >
                    <div className="flex items-center space-x-4 mb-3">
                      <span className="bg-navy-800 text-gold-600 px-3 py-1 rounded-full text-xs font-opensans font-medium">
                        {article.category}
                      </span>
                      <span className="text-gray-500 text-sm font-opensans">
                        {article.readTime}
                      </span>
                    </div>
                    <h3 className="font-playfair font-bold text-xl text-navy-800 mb-3">
                      {article.title}
                    </h3>
                    <p className="font-opensans text-gray-600 mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <SafeIcon icon={FiUser} className="w-4 h-4 text-gray-400" />
                        <span className="font-opensans text-sm text-gray-600">
                          {article.author}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-500">
                        <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                        <span className="font-opensans text-sm">
                          {article.date}
                        </span>
                      </div>
                    </div>
                    <button className="mt-4 inline-flex items-center text-gold-600 font-opensans font-semibold hover:text-gold-700 transition-colors">
                      Read Full Article
                      <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-2" />
                    </button>
                  </motion.article>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:w-1/3">
              <div className="space-y-8">
                {/* Categories */}
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="font-playfair font-bold text-xl text-navy-800 mb-4 flex items-center">
                    <SafeIcon icon={FiBookOpen} className="w-5 h-5 mr-2 text-gold-600" />
                    Categories
                  </h3>
                  <ul className="space-y-2">
                    {categories.map((category, index) => (
                      <li key={index}>
                        <button className="font-opensans text-gray-600 hover:text-gold-600 transition-colors text-left">
                          {category}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Newsletter Signup */}
                <div className="bg-navy-800 text-white p-6 rounded-lg">
                  <h3 className="font-playfair font-bold text-xl mb-4">
                    Legal Newsletter
                  </h3>
                  <p className="font-opensans text-gray-200 mb-4">
                    Subscribe to receive our latest legal insights and updates directly in your inbox.
                  </p>
                  <div className="space-y-3">
                    <input
                      type="email"
                      placeholder="Enter your email"
                      className="w-full px-4 py-2 rounded-lg text-gray-800 font-opensans"
                    />
                    <button className="w-full bg-gold-500 text-navy-800 px-4 py-2 rounded-lg font-opensans font-semibold hover:bg-gold-600 transition-colors">
                      Subscribe
                    </button>
                  </div>
                </div>

                {/* Popular Topics */}
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="font-playfair font-bold text-xl text-navy-800 mb-4 flex items-center">
                    <SafeIcon icon={FiTrendingUp} className="w-5 h-5 mr-2 text-gold-600" />
                    Trending Topics
                  </h3>
                  <ul className="space-y-3">
                    {[
                      'Employment Equity Regulations',
                      'POPIA Compliance',
                      'Corporate Governance',
                      'BBBEE Transformation',
                      'Commercial Litigation'
                    ].map((topic, index) => (
                      <li key={index}>
                        <button className="font-opensans text-gray-600 hover:text-gold-600 transition-colors text-left text-sm">
                          #{topic.replace(/\s+/g, '')}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-navy-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-playfair font-bold text-4xl mb-4">
            Need Legal Advice?
          </h2>
          <p className="font-opensans text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Our articles provide general guidance, but every legal situation is unique. Contact us for personalized legal advice tailored to your specific needs.
          </p>
          <Link
            to="/contact"
            className="bg-gold-500 text-navy-800 px-8 py-4 rounded-lg font-opensans font-semibold hover:bg-gold-600 transition-colors duration-200 inline-block"
          >
            Schedule Consultation
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Blog;